import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, Layers, Plus, Trash2, Save, RotateCcw, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin-only editor for the per-org Source Upload portal rules. Drives:
//  - Reports → Source Uploads chips / sub-tabs (Naukri / LinkedIn / Shine / Foundit / Other + any custom)
//  - Backend /api/reports/source-uploads classification at upload-time
//
// Slugs are lower-cased on save. Filename matching is case-insensitive,
// startsWith, prefix-based. Reserved slug "other" is the catch-all bucket
// and cannot be added explicitly.
const UploadSourceRulesCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/upload-sources`);
      setRules(res.data?.sources || []);
      setDefaults(res.data?.defaults || []);
      setDirty(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load upload source rules'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSlugChange = (idx, value) => {
    setDirty(true);
    setRules((prev) => prev.map((r, i) =>
      i === idx ? { ...r, slug: value.toLowerCase().replace(/[^a-z0-9_-]/g, '') } : r,
    ));
  };
  const handleLabelChange = (idx, value) => {
    setDirty(true);
    setRules((prev) => prev.map((r, i) => i === idx ? { ...r, label: value } : r));
  };
  const handleAddRow = () => {
    setDirty(true);
    setRules((prev) => [...prev, { slug: '', label: '' }]);
  };
  const handleRemoveRow = (idx) => {
    setDirty(true);
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };
  // Drag-and-drop reorder — kept minimal (HTML5 native, no library) so the
  // settings page stays light. The order matters for first-match semantics
  // (matching short slugs before long ones could shadow), so users can
  // resort if they add e.g. 'naukrigulf' alongside 'naukri'.
  const [dragIdx, setDragIdx] = useState(null);
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setRules((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
    setDirty(true);
  };
  const handleDragEnd = () => setDragIdx(null);

  const handleSave = async () => {
    // Client-side validation parity with the backend.
    const seen = new Set();
    for (const r of rules) {
      const slug = (r.slug || '').trim().toLowerCase();
      if (!slug) {
        toast.error('Every row needs a slug.'); return;
      }
      if (slug === 'other') {
        toast.error('"other" is reserved for the catch-all bucket.'); return;
      }
      if (seen.has(slug)) {
        toast.error(`Duplicate slug: ${slug}`); return;
      }
      seen.add(slug);
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/upload-sources`, { sources: rules });
      setRules(res.data?.sources || []);
      setDirty(false);
      toast.success('Source rules updated. Reports will reflect new buckets on next load.');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save rules'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    setRules(defaults.map((d) => ({ ...d })));
    setDirty(true);
  };

  return (
    <Card data-testid="upload-source-rules-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-teal-600" />
          Source Upload rules
        </CardTitle>
        <CardDescription>
          Configure the portal names that show up on the Reports → Source Uploads tab.
          When a recruiter bulk-uploads a resume, we match the filename's leading word
          (case-insensitive) against these slugs. Files that don't match any slug fall
          into <Badge variant="outline" className="ml-0.5 text-[10px]">Other</Badge>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="space-y-2" data-testid="upload-source-rules-list">
              {rules.map((r, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className="flex items-center gap-2 p-2 rounded-md border border-slate-200 bg-white hover:border-slate-300 transition"
                  data-testid={`source-rule-row-${idx}`}
                >
                  <GripVertical className="w-3.5 h-3.5 text-slate-300 cursor-move flex-shrink-0" />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2 items-center">
                    <Input
                      value={r.slug}
                      onChange={(e) => handleSlugChange(idx, e.target.value)}
                      placeholder="slug (e.g. hirist)"
                      className="h-8 text-xs font-mono"
                      data-testid={`source-rule-slug-${idx}`}
                      aria-label={`Slug for row ${idx + 1}`}
                    />
                    <Input
                      value={r.label}
                      onChange={(e) => handleLabelChange(idx, e.target.value)}
                      placeholder="Display label"
                      className="h-8 text-xs"
                      data-testid={`source-rule-label-${idx}`}
                      aria-label={`Label for row ${idx + 1}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveRow(idx)}
                    className="h-7 w-7 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`source-rule-remove-${idx}`}
                    aria-label={`Remove row ${idx + 1}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            {rules.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400 italic">
                No rules yet. Click Add row to start, or Reset to defaults.
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-3 border-t border-slate-100">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddRow}
                  disabled={rules.length >= 12 || saving}
                  data-testid="source-rule-add"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add row
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetToDefaults}
                  disabled={saving}
                  data-testid="source-rule-reset"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Reset
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {dirty ? (
                  <span className="text-[11px] font-medium text-amber-700" data-testid="source-rule-dirty">
                    Unsaved changes
                  </span>
                ) : null}
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  data-testid="source-rule-save"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {saving ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-3.5 h-3.5 mr-1" /> Save rules</>
                  )}
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Tip: Order matters. If two slugs could both match a filename, the earlier
              one wins. Cap is 12 portals.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UploadSourceRulesCard;
